import { FC, useEffect, useState } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';
import { useModal } from 'store/modal';
import { formatDate } from 'utils/helpers/formatters';
import { PaidLeaveDaysByYear, isIntigerOrFloat } from 'utils/helpers/leaves';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import { IFileDetails, ISelectOptionProps } from 'components/form/types';
import { getUserInfo } from 'utils/auth';
import { IInputs } from './types';

interface IProps {
  title: string;
  methods: any;
  handleSubmit: (data: IInputs) => void;
  onFilesUploaded: (file: IFileDetails[]) => void;
  onFilesDeleted: (file: File) => void;
  btnLabel: string;
  isEditMode?: boolean;
  types: ISelectOptionProps[];
  subTypes: ISelectOptionProps[];
  additioanlSubTypes?: ISelectOptionProps[];
  substituteEmployees?: ISelectOptionProps[];
}

const LeaveForm: FC<IProps> = ({
  title,
  methods,
  handleSubmit,
  onFilesUploaded,
  onFilesDeleted,
  btnLabel,
  types,
  subTypes,
  additioanlSubTypes,
  substituteEmployees,
}: IProps) => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(new Date());
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [showHalfDayOptions, setShowHalfDayOptions] = useState(false);
  const [holidaysCache, setHolidaysCache] = useState<{ [year: number]: string[] }>({});
  const [dynamicTypes, setDynamicTypes] = useState<ISelectOptionProps[]>([]);
  const selectedYear = startDate?.getFullYear();
  const { showModal } = useModal();
  const userInfo = getUserInfo();
  const isFeatureShowWFHLeaveTypeInMainDropdown = userInfo?.featureShowWFHLeaveTypeInMainDropdown;

  useEffect(() => {
    const adjustTypesForAdditionalSubTypes = () => {
      let newTypes: ISelectOptionProps[];

      const additionalSubTypesToCheck = isFeatureShowWFHLeaveTypeInMainDropdown
        ? additioanlSubTypes?.filter(
            subType => subType.value !== 'work_from_home' && subType.value !== 'work_from_office'
          )
        : additioanlSubTypes;

      if (additionalSubTypesToCheck && additionalSubTypesToCheck.length === 1) {
        const additionalTypeIndex = types.findIndex(type => type.value === 'additional');

        if (additionalTypeIndex !== -1) {
          newTypes = [...types];
          newTypes.splice(additionalTypeIndex, 1, ...additionalSubTypesToCheck);
        } else {
          newTypes = [...types];
        }
      } else if (
        isFeatureShowWFHLeaveTypeInMainDropdown &&
        (!additionalSubTypesToCheck || additionalSubTypesToCheck.length === 0)
      ) {
        newTypes = types.filter(type => type.value !== 'additional');
      } else {
        newTypes = [...types];
      }

      if (isFeatureShowWFHLeaveTypeInMainDropdown && additioanlSubTypes?.length) {
        const workFromHomeType = additioanlSubTypes.find(subType => subType.value === 'work_from_home');
        const workFromOfficeType = additioanlSubTypes.find(subType => subType.value === 'work_from_office');

        if (workFromHomeType && !newTypes.some(type => type.value === 'work_from_home')) {
          newTypes = [...newTypes, workFromHomeType];
        }

        if (workFromOfficeType && !newTypes.some(type => type.value === 'work_from_office')) {
          newTypes = [...newTypes, workFromOfficeType];
        }
      }

      setDynamicTypes(newTypes);
    };

    adjustTypesForAdditionalSubTypes();
  }, [types, additioanlSubTypes, isFeatureShowWFHLeaveTypeInMainDropdown]);

  useEffect(() => {
    const fetchHolidays = async () => {
      if (holidaysCache[selectedYear]) {
        return;
      }

      try {
        const response = await API.get(`holidays/${selectedYear}`);
        const holidayDates = response.data.items.map((item: any) => item.date);
        setHolidaysCache(prevCache => ({
          ...prevCache,
          [selectedYear]: holidayDates,
        }));
      } catch (error) {
        showModal({
          type: 'error',
          content: createErrorFromResponse(error).message,
        });
      }
    };

    fetchHolidays();
  }, [selectedYear, holidaysCache]);

  const renderUnusedDaysPaidLeave = (allPaidLeaveDaysByYear: PaidLeaveDaysByYear[]) => {
    const filteredEntries = allPaidLeaveDaysByYear
      ?.filter((entry: PaidLeaveDaysByYear) => entry.days > 0)
      .sort((a, b) => a.year - b.year);
    const renderEntry = (entry: PaidLeaveDaysByYear, index: number) => {
      if (entry.days === 0) return null;

      const dayString = entry.days === 1 ? t('leaves.day') : t('leaves.days');

      return `${t('leaves.from')} ${entry.year}${t('leaves.yearShort')} - ${isIntigerOrFloat(entry.days)} ${dayString}`;
    };

    return filteredEntries.map((entry: PaidLeaveDaysByYear, i: number) => {
      return (
        <span
          className="d-sm-inline d-md-block d-xl-inline"
          style={{
            marginBottom: '5px',
            fontWeight: 'lighter',
          }}
        >
          {renderEntry(entry, i)}
        </span>
      );
    });
  };

  const isHoliday = (date: Date) => {
    const day = date.getDay();
    const formattedDate = formatDate(date, 'YYY-mm-dd');
    const holidaysForYear = holidaysCache[date.getFullYear()] || [];
    // Return true if it's not a weekend and not a holiday, false otherwise
    return !(day === 0 || day === 6 || holidaysForYear.includes(formattedDate));
  };

  const isAllowedDate = (date: Date) => {
    const isNotHolidayOrWeekend = isHoliday(date);
    const isOutsideDisallowedRange =
      !shouldDisallowLeaveDays || !disallowedFrom || !disallowedTo || date < disallowedFrom || date > disallowedTo;

    return isNotHolidayOrWeekend && isOutsideDisallowedRange;
  };

  const { watch, setValue } = methods;
  const fieldType = useWatch({ control: methods.control, name: 'type' }) as ISelectOptionProps;
  const subFieldType = useWatch({ control: methods.control, name: 'sub_type' }) as ISelectOptionProps;
  const isHalfDayChecked = watch('half_day') as ISelectOptionProps;
  const leaveTypes: { [key: string]: string } = {
    sick: 'sick',
    paid: 'paid',
    unpaid: 'unpaid',
    additional: 'additional',
  };
  const isRequireLeaveSubstituteEmployee = userInfo?.featureRequireLeaveSubstituteEmployee;
  const isArt157LeaveWithoutAttachment = userInfo?.featureArt157LeaveWithoutAttachment;
  let isAttachmentsRequired =
    fieldType?.value === leaveTypes.sick ||
    (fieldType?.value === leaveTypes.paid && subFieldType?.value !== 'paid_ordinary');
  const showSubType = fieldType?.value === leaveTypes.paid || fieldType?.value === leaveTypes.additional;
  const isWorkFromHomeInMainDropdown = dynamicTypes.some(type => type.value === 'work_from_home');
  const isWorkFromOfficeInMainDropdown = dynamicTypes.some(type => type.value === 'work_from_office');
  const additionalSubTypesForDropdown =
    (isWorkFromHomeInMainDropdown || isWorkFromOfficeInMainDropdown) && additioanlSubTypes
      ? additioanlSubTypes.filter(subType => subType.value !== 'work_from_home' && subType.value !== 'work_from_office')
      : additioanlSubTypes;
  const currentSubTypes = fieldType?.value === leaveTypes.paid ? subTypes : additionalSubTypesForDropdown;
  if (
    subFieldType?.value === 'paid_served_time' ||
    subFieldType?.value === 'paid_additional' ||
    subFieldType?.value === 'irregular_working_hours' ||
    subFieldType?.value === 'specific_working_conditions'
  ) {
    isAttachmentsRequired = false;
  }

  if (
    isArt157LeaveWithoutAttachment &&
    (subFieldType?.value === 'paid_marriage' ||
      subFieldType?.value === 'paid_death' ||
      subFieldType?.value === 'paid_blood_donation')
  ) {
    isAttachmentsRequired = false;
  }

  const disallowedFrom = userInfo?.disallowedLeavesFrom ? new Date(userInfo.disallowedLeavesFrom) : null;
  const disallowedTo = userInfo?.disallowedLeavesTo ? new Date(userInfo.disallowedLeavesTo) : null;
  const shouldDisallowLeaveDays = userInfo?.shouldDisallowLeaveDays;

  const showHalfDayLeaveOption =
    userInfo.featureLeavesHalfDayOption &&
    (fieldType?.value === leaveTypes.unpaid ||
      (fieldType?.value === leaveTypes.paid && subFieldType?.value === 'paid_ordinary'));

  const isSickLeaveWithoutAttachment = userInfo.featureSickLeaveWithoutAttachment;

  const selectedLeaveType = watch('type');
  const selectedSubType = watch('sub_type');
  const isSickLeave = selectedLeaveType?.value === 'sick';
  const isWorkFromHome =
    (isWorkFromHomeInMainDropdown && selectedLeaveType?.value === 'work_from_home') ||
    selectedSubType?.value === 'work_from_home';
  const isWorkFromOffice =
    (isWorkFromOfficeInMainDropdown && selectedLeaveType?.value === 'work_from_office') ||
    selectedSubType?.value === 'work_from_office';
  const shouldBypassDateRestrictions = isSickLeave || isWorkFromHome || isWorkFromOffice;

  // Get hint for sub_type based on the selected value
  const getSubTypeHint = () => {
    const subTypeValue = subFieldType?.value;
    if (!subTypeValue || typeof subTypeValue !== 'string') return undefined;

    const hintsMap: { [key: string]: string } = {
      paid_ordinary: 'leaves.paid_ordinary_hint',
      paid_blood_donation: 'leaves.paid_blood_donation_hint',
      paid_marriage: 'leaves.paid_marriage_hint',
      paid_death: 'leaves.paid_death_hint',
      irregular_working_hours: 'leaves.irregular_working_hours_hint',
      specific_working_conditions: 'leaves.specific_working_conditions_hint',
      court_summons: 'leaves.court_summons_hint',
    };

    return hintsMap[subTypeValue] ? t(hintsMap[subTypeValue]) : undefined;
  };

  const showSubstituteDropdown =
    userInfo?.featureLeaveSubstituteEmployee &&
    (fieldType?.value === leaveTypes.paid || fieldType?.value === leaveTypes.unpaid);

  useEffect(() => {
    if (!showSubstituteDropdown) {
      setValue('substitute_employee_id', null);
    }
  }, [showSubstituteDropdown, setValue]);

  if (isHalfDayChecked) {
    // Reset dateTo if halfDay is checked
    setValue('date_to', '');
  } else {
    // Reset portionOfDay if halfDay is NOT checked
    setValue('part_of_day', null);
  }

  useEffect(() => {
    if (!showHalfDayLeaveOption) {
      setValue('half_day', false);
    }
  }, [showHalfDayLeaveOption, setValue]);

  useEffect(() => {
    if (isHalfDayChecked && showHalfDayLeaveOption) {
      setShowHalfDayOptions(true);
      return;
    }

    setShowHalfDayOptions(false);
  }, [isHalfDayChecked, showHalfDayLeaveOption]);

  useEffect(() => {
    setValue('sub_type', { label: '', value: '' });
    methods.clearErrors('sub_type');
  }, [fieldType?.value]);

  return (
    <LeaveFormContainer>
      <StyledFormGroup>
        <Form
          title={title}
          methods={methods}
          onSubmit={handleSubmit}
          btnLabel={btnLabel}
          isHorizontal
          showFileApprovalText={fieldType?.value === leaveTypes.sick && !isSickLeaveWithoutAttachment}
          showNoFileApprovalText={fieldType?.value === leaveTypes.sick && isSickLeaveWithoutAttachment}
          showPaidFileApprovalText={
            (subFieldType?.value === 'paid_blood_donation' ||
              subFieldType?.value === 'paid_marriage' ||
              subFieldType?.value === 'paid_death') &&
            !isArt157LeaveWithoutAttachment
          }
          isUploading={isFileUploading}
        >
          <FormGroup
            controlId="formGroupType"
            label={t(isFeatureShowWFHLeaveTypeInMainDropdown ? 'leaves.typeShort' : 'leaves.type')}
            type="select"
            name="type"
            selectOptions={dynamicTypes}
            isRequired
            isHorizontal
            formGroupClassName="mb-2 mr-1"
            testId="leave-type-select"
          />
          {showSubType && (
            <FormGroup
              controlId="formGroupSubType"
              label={t(isFeatureShowWFHLeaveTypeInMainDropdown ? 'leaves.subTypeShort' : 'leaves.subType')}
              type="select"
              name="sub_type"
              selectOptions={currentSubTypes}
              isRequired
              isHorizontal
              formGroupClassName="mb-2 mr-1"
              hint={getSubTypeHint()}
              testId="leave-sub-type-select"
            />
          )}
          {showHalfDayLeaveOption && (
            <FormGroup
              controlId="formGroupHalfDay"
              label={t('leaves.requestHalfDay')}
              checkType="checkbox"
              name="half_day"
              isHorizontal
              options={[
                {
                  label: '',
                  name: 'half_day',
                  id: 'half_day',
                  value: 'true',
                },
              ]}
              controlGrid={1}
              formGroupClassName="mb-2 mr-1"
            >
              {showHalfDayOptions && (
                <FormGroup
                  controlId="formGroupPortionOfDay"
                  label={t('leaves.portionOfDay')}
                  type="select"
                  name="part_of_day"
                  selectOptions={[
                    { label: t('leaves.firstPortionOfDay'), value: 'first_part' },
                    { label: t('leaves.secondPortionOfDay'), value: 'second_part' },
                  ]}
                  placeholder={t('form.select')}
                  isRequired={!!isHalfDayChecked}
                  isHorizontal
                  formGroupClassName="col-sm-5 mb-0 p-md-0 nested-form-group"
                  labelGrid={4}
                />
              )}
            </FormGroup>
          )}
          <FormGroup
            controlId="formGroupDateFrom"
            label={t('leaves.dateFrom')}
            type="datepicker"
            name="date_from"
            onChange={date => {
              methods.clearErrors();
              setStartDate(date);
            }}
            filterDate={shouldBypassDateRestrictions ? undefined : isAllowedDate}
            dateFormat="dd.MM.yyyy"
            isRequired
            isHorizontal
            formGroupClassName="mb-2 mr-1"
            labelGrid={2}
            controlGrid={2}
          >
            <FormGroup
              controlId="formGroupDateTo"
              label={t('leaves.dateTo')}
              type="datepicker"
              name="date_to"
              minDate={startDate}
              maxDate={new Date(selectedYear, 11, 31)}
              onChange={date => {
                methods.clearErrors();
              }}
              filterDate={shouldBypassDateRestrictions ? undefined : isAllowedDate}
              dateFormat="dd.MM.yyyy"
              isRequired={!isHalfDayChecked}
              isHorizontal
              formGroupClassName={`mb-0 mr-1 p-md-0 col-sm-4 nested-form-group${isHalfDayChecked ? ' d-none' : ''}`}
              // if halfDay is checked, disable dateTo
              isDisabled={!!isHalfDayChecked}
              labelGrid={7}
            />
          </FormGroup>
          {showSubstituteDropdown && (
            <FormGroup
              controlId="formGroupSubstituteEmployee"
              label={t('leaves.substituteEmployee')}
              type="select"
              name="substitute_employee_id"
              selectOptions={substituteEmployees}
              isHorizontal
              isSearchable
              isClearable
              isRequired={isRequireLeaveSubstituteEmployee}
              placeholder={t('form.select')}
              formGroupClassName="mb-2 mr-1"
            />
          )}
          <FormGroup
            controlId="formGroupComment"
            label={t('leaves.comment')}
            type="text"
            name="comment"
            maxLength={255}
            isHorizontal
            formGroupClassName="mb-2 mr-1"
          />
          <Row>
            <Col lg={{ span: 'auto', offset: 2 }}>
              <p>
                <i>{t('leaves.commentInfo')}</i>
              </p>
            </Col>
          </Row>
          <Row className="mt-1 file-attachment-container">
            <Col lg={{ span: 8, offset: 1 }}>
              <hr className="mb-3 mt-3" />
              <h4 className="text-center mb-3">
                {(isSickLeaveWithoutAttachment ? false : isAttachmentsRequired) && <span>* </span>}
                {t('docWizard.step3.attachDocuments')}
              </h4>

              <FormGroup
                formGroupClassName="leave-form-dropzone"
                controlId="formGroupFileUpload"
                type="file"
                placeholder={t('fileUploader.placeholder')}
                name="file"
                isHorizontal
                accept={['application/pdf', '.jpg', '.png', '.heic']}
                multiple
                isRequired={isSickLeaveWithoutAttachment ? false : isAttachmentsRequired}
                uploadUri="leaves/upload-attachment"
                deleteFileUri="leaves/attachments"
                hint={t('fileUploader.hint', { accept: 'pdf, jpg, png, heic' })}
                onFilesUploaded={onFilesUploaded}
                onFilesDeleted={onFilesDeleted}
                controlGrid={12}
                isFileUploading={(isUploading: boolean) => {
                  setIsFileUploading(isUploading);
                }}
              />
            </Col>
          </Row>
        </Form>
      </StyledFormGroup>
      <StyledNoteSection>
        <div className="d-block d-xl-inline styled-note">
          <b>
            {t('leaves.note')}
            {userInfo.paidLeaveDays}
          </b>
        </div>
        <div className="styled-note d-flex flex-column">
          {renderUnusedDaysPaidLeave(userInfo.paidLeaveDaysByYear || [])}
        </div>
        {userInfo.featureLeaveDaysServedTime && (
          <StyledNote>
            {t('leaves.noteTimeServed')}
            {userInfo.paidLeaveDaysServedTime}
          </StyledNote>
        )}
        {userInfo.featureLeaveDaysAdditionalLeave && (
          <StyledNote>
            {t('leaves.noteAdditionalLeave')}
            {userInfo.paidLeaveDaysAdditionalLeave}
          </StyledNote>
        )}
        {userInfo.featureLeaveDaysIrregularWorkingHours && (userInfo.paidLeaveDaysIrregularWorkingHours ?? 0) > 0 && (
          <StyledNote>
            {t('leaves.noteIrregularWorkingHours')}
            {userInfo.paidLeaveDaysIrregularWorkingHours}
          </StyledNote>
        )}
        {userInfo.featureLeaveDaysSpecificWorkingConditions &&
          (userInfo.paidLeaveDaysSpecificWorkingConditions ?? 0) > 0 && (
            <StyledNote>
              {t('leaves.noteSpecificWorkingConditions')}
              {userInfo.paidLeaveDaysSpecificWorkingConditions}
            </StyledNote>
          )}
        {userInfo.featureLimitWfhLeaveDays && userInfo.wfhLimitType !== 'none' && (
          <StyledNote>
            {userInfo.wfhLimitType === 'week'
              ? `${t('leaves.noteWfhWeekly')}${userInfo.remainingWfhLeavesPerWeek}`
              : `${t('leaves.noteWfhMonthly')}${userInfo.remainingWfhLeavesPerMonth}`}
          </StyledNote>
        )}
      </StyledNoteSection>
    </LeaveFormContainer>
  );
};

const LeaveFormContainer = styled.div`
  position: relative;

  .file-attachment-container {
    margin-right: 12%;
  }

  @media ${props => props.theme.breakpoints.lg} {
    .file-attachment-container {
      margin-right: 0;
    }
  }
`;

const StyledNoteSection = styled.div`
  position: absolute;
  right: 0;
  top: 60px;
  width: 30%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;

  .styled-note {
    font-size: 1rem;
  }

  @media ${props => props.theme.breakpoints.lg} {
    display: none;
  }
`;

const StyledNote = styled.p`
  font-size: 1rem;
  font-weight: bold;
  align-items: flex-start;
  flex: 1;
  padding-right: 10px;

  @media ${props => props.theme.breakpoints.md} {
    text-align: center;
  }
`;

const StyledFormGroup = styled.div`
  .leave-form-dropzone {
    > [class^='col-'] > div {
      min-height: 121px;
    }

    input[type='file'] {
      ~ p {
        margin: 1rem 0 !important;
      }

      ~ div {
        &:not(:empty) {
          margin-top: -0.5rem;
        }

        > div {
          padding: 0.5rem;
        }
      }
    }
  }

  form > hr {
    margin: 0 0 1rem;
  }

  .nested-form-group {
    > div {
      flex: 1;
      padding: 0;
      max-width: 100%;
    }

    @media ${props => props.theme.breakpoints.md} {
      > label {
        line-height: 1;
      }
    }
    @media ${props => props.theme.breakpoints.sm} {
      margin: 0;

      > label {
        padding-left: 0;
      }
    }
  }

  .react-datepicker__day--weekend {
    color: #d1d1d1; /* Gray color for weekends */
  }
`;

export default LeaveForm;
